import { Box, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton } from "@mui/material";
import Modal from '../modal/modal';
import Typography from "../typography/typography";
import DynamicInput from '../form-control/form-builder/form-builder-element/text';
import DynamicRadioButton from '../form-control/form-builder/form-builder-element/radio-button';
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generateValidationSchema, generateValidationSchema1 } from "./validator";
import DynamicSearchSelect from "../form-control/form-builder/form-builder-element/dynamic-select";
import DynamicPhone from '@/components/form-control/form-builder/form-builder-element/phone';
import Accordion from "../accordion/accordion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import './parties-modal.scss';
import { useAccountingSetting } from "../../hooks/useAccountSetting";
import { useAuth } from '@erpsquad/common/runtime';
import { checkUniqueFields } from '../../utils/common';
import { getApiConfigSafe } from '../../utils/api-config';
import {
  addressColumns,
  addressOmitKeys,
  addressPayload,
  contactColumns,
  contactOmitKeys,
  contactPayload,
  ksaAddressPayload,
  PARTIES_FIELDS,
  PARTIES_FORM_ARRAY_NAME,
  partyType,
  processPartiesPayload
} from "./utils/constant";
import MaterialTable from "../material-table/material-table";
import parsePhoneNumberFromString from "libphonenumber-js";
import Checkbox from "../checkbox/checkbox";
import { transformTableColumns } from "../../utils/common-utility";
import { createParties } from "../../redux/actionCreator";
import { useDispatch } from "react-redux";
import { enqueueSnackbar } from 'notistack';
import { getErrorMessage } from "../../utils/common";
import ModalLoader from "../loaders/modal-loader";
import Button from "../button/button";
import { Close } from "@mui/icons-material";

interface CustomerVendorModalProps {
  isOpen: boolean;
  handleModalClose?: () => void;
  modalProps?: any;
  type?: string;
  partyCreated?: () => void;
  isLoading?: boolean;
  onSave?: (payload: any) => Promise<void>;
  setIsOpen?: (value: boolean) => void;
}

interface LocationState {
  state: any;
  country: any;
}

const defaultLocationState: LocationState = { state: '', country: '' };

export default function CustomerVendorModal({
  isOpen,
  handleModalClose,
  modalProps,
  type,
  partyCreated,
  isLoading,
  onSave,
  setIsOpen
}: CustomerVendorModalProps) {

  //Hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isKSA = getApiConfigSafe()?.countryEnv === 'ksa';

  //Refs
  const locationRef = useRef<LocationState>(defaultLocationState);
  const isAddressInitialized = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      isAddressInitialized.current = false;
      locationRef.current = { state: '', country: '' };
    }
  }, [isOpen]);

  //Local states
  const [addressRows, setAddressRows] = useState<any[]>([]);
  const [contactRows, setContactRows] = useState<any[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedCompanyData, setSelectedCompanyData] = useState<any[]>([]);
  const lastCheckedValues = useRef<Record<string, any>>({})

  //Form
  const {
    control,
    formState: { errors },
    trigger,
    getValues,
    handleSubmit,
    setValue,
    resetField,
    reset,
    watch,
    getFieldState,
    setError

  } = useForm({
  defaultValues: {
    status: true,
    parties: {
      account_type: 'Company',
    },
  },
    mode: 'all',
    resolver: generateValidationSchema1()
  });

  const fieldToUpdate = {
    [PARTIES_FIELDS.RECEIVABLE_ACCOUNT]: 'receivable_account',
    [PARTIES_FIELDS.TAX_TEMPLATE]: type?.toLowerCase() === 'vendor' ? 'purchase_tax' : 'sales_tax',
  };

  useAccountingSetting({
    fieldToUpdate,
    companyField: PARTIES_FIELDS.COMPANY,
    fieldArrayName: PARTIES_FORM_ARRAY_NAME,
    formMethods: { control, setValue, watch, getFieldState },
    ...({ isUpdateAccount: true } as any),
  });


  const selectedType = useWatch({
    control,
    name: 'parties.account_type',
  });

  const responsiblePerson = useWatch({
    control,
    name: `${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.RESPONSIBLE_PERSON}`,
  });

  const mainContactNo = useWatch({
    control,
    name: `${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.CONTACT_NO}`,
  });

  const mainEmailIds = useWatch({
    control,
    name: `${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.EMAIL_IDS}`,
  });

  const checkUniqueness = useCallback(async () => {
    const values = getValues()
    const formValues = values?.[PARTIES_FORM_ARRAY_NAME]
    const fieldsToCheck = ['crn', 'email_ids', 'company_name']
    const allFields = ['crn', 'email_ids', 'contact_no', 'company_name']

    const nothingChanged = fieldsToCheck.every(
      (field) => lastCheckedValues.current[field] === formValues?.[field]
    )
    if (nothingChanged) return true

    const payload = {}
    allFields.forEach((field) => (payload[field] = formValues?.[field]))

    const response = await checkUniqueFields(payload)
    let isUnique = true

    for (const [field, exists] of Object.entries(response)) {
      if (exists) {
        setError(`${PARTIES_FORM_ARRAY_NAME}.${field}`, {
          type: 'manual',
          message: `${field.replace(/_/g, ' ').toLowerCase()} already exists`,
        })
        isUnique = false
      }
    }

    lastCheckedValues.current = { ...formValues }

    return isUnique
  }, [getValues, setError])

  const handleFieldBlur = useCallback(
    async (fieldName: string) => {
      const uniquenessFields = ['crn', 'email_ids', 'company_name']
      if (!uniquenessFields.includes(fieldName)) return
      await checkUniqueness()
    },
    [checkUniqueness]
  )

  //Call Back functions
  const tableColumns = useCallback(
    (columns: any, tableRows: any) => {
      const phoneNumberCache = new Map();

      return transformTableColumns({
        columns,
        currencySymbol: undefined,
        translationFn: t,
        enableFooter: false,
        redirectionLink: undefined,
        redirectionLinkState: undefined,
        rows: tableRows,
        statusClasses: undefined,
        customizeValue: (row, columnAccessorKey, value) => {
          switch (columnAccessorKey) {
            case 'default_shipping_address':
            case 'default_billing_address': {
              return (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Checkbox
                    checked={Boolean(value)}
                    sx={{ pointerEvents: 'none' }}
                  />
                </Box>
              );
            }

            case 'phone':
            case 'contact_no': {
              if (!value) return '';
              const phoneKey = `phone-${value}`;
              if (!phoneNumberCache.has(phoneKey)) {
                const parsed = parsePhoneNumberFromString(`+${value}`);
                phoneNumberCache.set(phoneKey, parsed?.nationalNumber || '');
              }
              return phoneNumberCache.get(phoneKey);
            }

            case 'country_code': {
              const phone = row?.original?.phone || row?.original?.contact_no;
              if (!phone) return '';
              const countryCodeKey = `country-${phone}`;
              if (!phoneNumberCache.has(countryCodeKey)) {
                const parsed = parsePhoneNumberFromString(`+${phone}`);
                phoneNumberCache.set(countryCodeKey,
                  parsed?.countryCallingCode ? `+${parsed.countryCallingCode}` : ''
                );
              }
              return phoneNumberCache.get(countryCodeKey);
            }

            default:
              return value;
          }
        }
      });
    },
    [t]
  );

  //Methods

  const handleRowOperation = async (
    eData: any,
    state: any[],
    setState: React.Dispatch<React.SetStateAction<any[]>>,
    isAddress: boolean
  ) => {
    try {
      const { values, row } = eData;
      const editRow = row.index >= 0;
      const payload = isAddress ? (isKSA ? ksaAddressPayload : addressPayload) : contactPayload;

      const processedValues: any = {};
      for (const key in payload) {
        if (_.has(values, key)) {
          processedValues[key] = values?.[key] ? values[key] : null;
        }
      }

      if (isAddress) {
        const dropdowns = {
          c: locationRef.current?.country,
          s: locationRef.current?.state,
        };

        const validatedValues = await validateAddressDefaults(processedValues, state, row.index);
        const addressValues = {
          ...validatedValues,
          ...(dropdowns.c?.id ? {
            country_name: dropdowns.c.name,
            country: dropdowns.c.id,
            country_data: dropdowns.c,
            country_id: dropdowns.c.id,
          } : {}),
          ...(dropdowns.s?.id ? {
            state_name: dropdowns.s.name,
            state: dropdowns.s.id,
            state_data: dropdowns.s,
            state_id: dropdowns.s.id,
          } : {}),
        };

        if (editRow) {
          return setState(state.map((ar, i) => i === row.index ? { ...ar, ...addressValues } : ar));
        }
        return setState([...state, addressValues]);
      }

      if (editRow) {
        return setState(state.map((ar, i) => i === row.index ? { ...ar, ...processedValues } : ar));
      }
      return setState([...state, processedValues]);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const validateAddressDefaults = async (values: any, state: any[], index: number) => {
    let updatedValues = { ...values };
    const validate = (type: "shipping" | "billing") => {
      const u_data = [...state];
      const field = type === 'shipping' ? 'default_shipping_address' : 'default_billing_address';
      if (index >= 0) {
        u_data.splice(index, 1);
      }
      const hasDefault = u_data.some((d: any) => d?.[field]);

      if (hasDefault && values?.[field]) {
        throw new Error(
          `Another address is already marked as the default ${type} address. Please uncheck that before selecting this one`
        );
      }
      else if (!hasDefault) {
        updatedValues = {
          ...updatedValues,
          [field]: values?.[field] || state?.length == 0 ? 1 : 0,
        };
      }

    };

    await validate("shipping");
    await validate("billing");

    return updatedValues;
  };

  const deleteRow = async (
    state: any[],
    setState: React.Dispatch<React.SetStateAction<any[]>>,
    row: any
  ) => {
    const rowIndex = row.index;
    const updatedData = state.filter((_, i) => i !== rowIndex);
    setState(updatedData);
    return Promise.resolve();
  };

  const onChangeAddressTableField = async (e: any, t: any, r: any, col: any, selectedData: any) => {
    const fieldName = e?.target?.name;
    let updatedValues: any = null;

    if (col?.editProperties?.field_name) {
      locationRef.current = {
        ...locationRef.current,
        [col.editProperties.field_name]: selectedData,
      };
    }

    if (fieldName === 'country') {
      updatedValues = {
        ...r._valuesCache,
        country: selectedData?.id,
        country_id: selectedData?.id,
        state: null,
        state_id: null,
        state_name: null,
      };
      locationRef.current = {
        ...locationRef.current,
        state: null,
      };
    } else if (fieldName === 'state') {
      updatedValues = {
        ...r._valuesCache,
        state: selectedData?.id,
        state_id: selectedData?.id
      };
    } else if (fieldName === 'phone') {
      const p = parsePhoneNumberFromString(`+${e}`);
      updatedValues = {
        ...r._valuesCache,
        country_code: p?.countryCallingCode ? `+${p.countryCallingCode}` : '',
      };
    }

    const summaryFields = [
      'contact_name',
      'street_1',
      'street_2',
      'street_3',
      'street_name',
      'building_no',
      'additional_no',
      'national_address',
      'zip_code',
      'city',
      'district_name',
      'state',
      'country'
    ];

    if (r._valuesCache) {
      const fieldValues = summaryFields.map(field => {
        if (field === 'country') {
          return locationRef.current?.country?.name || '';
        } else if (field === 'state') {
          return locationRef.current?.state?.name || '';
        } else if (field === fieldName) {
          return e.target?.value || '';
        }
        return r._valuesCache?.[field] || '';
      });

      const summary = fieldValues.reduce((acc, val, idx) => {
        if (!val) return acc;
        if (!acc) return val;

        val = val?.name || val;
        acc = acc?.name || acc;

        return [2, 3, 4, 5, 6, 7].includes(idx) ? `${acc} ${val}` : `${acc}, \n${val}`;
      }, '');

      r._valuesCache.summary = summary;
    }

    if (updatedValues) {
      r.getAllCells().forEach((c: any) => {
        if (_.has(updatedValues, c.column.id)) {
          r._valuesCache[c.column.id] = updatedValues[c.column.id];
        }
      });
    }

    if (fieldName === 'country' && selectedData?.id) {
      r._valuesCache['country_id'] = selectedData.id;
    }

    return r._valuesCache;
  };



  const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const onSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    const isUnique = await checkUniqueness()
    if (!isUnique) return

    const values = getValues();
    const formValues = values?.[PARTIES_FORM_ARRAY_NAME];
    let emailArray: string[] = [];

    if (formValues?.email_ids) {
      emailArray = formValues?.email_ids
        ?.split(/\s*,\s*/)
        .map((email_id: any) => email_id?.replaceAll(',', '')?.trim())
        .filter((email_id: any) => email_id.match(EMAIL_REGEX));
    }

    const payload = {
      party: {
        ...processPartiesPayload(formValues),
        email_ids: emailArray,
        account_type: selectedType,
        status: 1,
        type,
        company_id: formValues?.[PARTIES_FIELDS.COMPANY] || null
      }
    };
    if (typeof onSave == 'function') {
      onSave?.(payload);
      return;
    }
    setIsCreating(true);
    try {

      const res = await dispatch(createParties(payload));

      if (res?.meta?.requestStatus === 'fulfilled') {
        enqueueSnackbar(`${type} ${t('common.party.msg.success')}`);
        closeModal();
        partyCreated();
      } else if (res?.meta?.requestStatus === 'rejected') {
        const errorMessage = getErrorMessage(res?.error?.message);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setIsCreating(false);
    }
  };

  const closeModal = () => {
    resetForm()
    setAddressRows([]);
    setContactRows([]);
    locationRef.current = { state: '', country: '' };
    isAddressInitialized.current = false;
    handleModalClose?.();
    setIsOpen?.(false);
  }
  const resetForm = () => {
    reset();
  }

  //Use Effect
  useEffect(() => {
    const addressData = addressRows.map(a => _.omit(a, addressOmitKeys));
    setValue(`${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.ADDRESS}`, addressData);
  }, [addressRows, setValue]);

  useEffect(() => {
    const contactData = contactRows.map(c => _.omit(c, contactOmitKeys));
    setValue(`${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.CONTACTS}`, contactData);
  }, [contactRows, setValue]);

  useEffect(() => {
    setIsCreating(isLoading)
  }, [isLoading]);


  useEffect(() => {
    if (selectedType === 'Individual') {
      // Clear Company fields
      resetField('parties.company_name');
      resetField('parties.vat_number');
      resetField('parties.crn');
    }

    if (selectedType === 'Company') {
      // Clear Individual fields
      resetField('parties.first_name');
      resetField('parties.last_name');
    }
  }, [selectedType, resetField]);

// Pre-fill company and currency from user data when modal opens
useEffect(() => {
		if (isOpen && user?.company_data) {
			const companyId =
				user?.entity_priority ||
				user?.company_data?.id ||
				(Array.isArray(user?.company_data) && user?.company_data[0]?.id) ||
				null;


			if (companyId) {
				setValue('quotation.company_id', companyId);
        setValue(`${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.COMPANY}`, companyId);
			}
		}
  }, [isOpen, user?.company_data, setValue]);

  useEffect(() => {
    if (isOpen && user?.company_currencies) {
      const currencies = Object.values(user.company_currencies);
      if (currencies.length > 0) {
        setValue(`${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.CURRENCY}`, [currencies[0].id]);
      }
    }
  }, [isOpen, user?.company_currencies , setValue]);

  // Auto fill first contact row
  useEffect(() => {
    const firstContactRow = contactRows[0];
    const parsedCountryCode = mainContactNo
      ? `+${parsePhoneNumberFromString(`+${mainContactNo}`)?.countryCallingCode || ''}`
      : '';

    const firstEmail = mainEmailIds
      ? mainEmailIds.split(/\s*,\s*/).map((e: string) => e.trim()).filter((e: string) => EMAIL_REGEX.test(e))[0] || ''
      : '';

    const updatedFirstRow = {
      name: responsiblePerson || '',
      contact_no: mainContactNo || '',
      email: firstEmail,
      country_code: parsedCountryCode,
    };

    if (firstContactRow) {
      const hasChanged = Object.keys(updatedFirstRow).some(
        (key) => firstContactRow[key] !== updatedFirstRow[key]
      );
      if (hasChanged) {
        const updatedRows = [...contactRows];
        updatedRows[0] = { ...updatedRows[0], ...updatedFirstRow };
        setContactRows(updatedRows);
      }
    } else {
      setContactRows([updatedFirstRow, ...contactRows]);
    }
  }, [responsiblePerson, mainContactNo, mainEmailIds]);

  // Auto fill first Address Row
  useEffect(() => {
    const firstRow = addressRows[0];
    const hasAddressDetails = firstRow?.street_1 || firstRow?.city;

    if (responsiblePerson && mainContactNo) {
      const parsedCountryCode = mainContactNo
        ? `+${parsePhoneNumberFromString(`+${mainContactNo}`)?.countryCallingCode || ''}`
        : '';

      // Create first row if doesn't exist yet
      if (!firstRow) {
        if (!isAddressInitialized.current) {
          setAddressRows([{
          ...(isKSA ? ksaAddressPayload : addressPayload),
          contact_name: responsiblePerson,
          phone: mainContactNo,
          country_code: parsedCountryCode,
          country: user?.country_data?.id || null,
          country_name: user?.country_data?.name || null,
          country_id: user?.country_data?.id || null,
          country_data: user?.country_data || null,
        }]);
        if (!locationRef.current?.country && user?.country_data) {
          locationRef.current = {
            ...locationRef.current,
            country: user.country_data,
          };
        }
        isAddressInitialized.current = true;
        }
        return;
      }

      // Update first row ONLY if address details are not yet filled
      if (!hasAddressDetails) {
        const contactChanged = firstRow?.contact_name !== responsiblePerson;
        const phoneChanged = firstRow?.phone !== mainContactNo;

        if (contactChanged || phoneChanged) {
          setAddressRows(prev => prev.map((row, idx) =>
            idx === 0
              ? { ...row, contact_name: responsiblePerson, phone: mainContactNo, country_code: parsedCountryCode }
              : row
          ));
        }
      }
    }
  }, [responsiblePerson, mainContactNo, user, addressRows]);

  // Auto-populate address row with country on modal open
  useEffect(() => {
    if (isOpen && user?.country_data && addressRows.length === 0 && !isAddressInitialized.current) {
      setAddressRows([{
        ...(isKSA ? ksaAddressPayload : addressPayload),
        country: user.country_data.id,
        country_name: user.country_data.name,
        country_id: user.country_data.id,
        country_data: user.country_data,
        default_shipping_address: true,
        default_billing_address: true,
      }]);
      if (!locationRef.current?.country) {
        locationRef.current = {
          ...locationRef.current,
          country: user.country_data,
        };
      }
      isAddressInitialized.current = true;
    }
  }, [isOpen, user, addressRows.length]);

  const basicDetailsSection = useMemo(() => (
    <Accordion
      title={
        <Typography type="s3" weight="medium" color="theme.secondary.1000">
          {t("common.party.sections.basic_details")}
        </Typography>
      }
      accordionProps={{
        defaultExpanded: true,
        className: "form--Accordion",
      }}
    >
      <Grid container spacing={2}>

        <Grid item xs={12} sm={12}>
          <DynamicRadioButton
            label=''
            name={PARTIES_FIELDS.TYPE}
            fieldArrayName={PARTIES_FORM_ARRAY_NAME}
            formControl={control}
            options={partyType}
            display='row'
            title={t(t('common.party.fields.type'))}
            titlePosition='start'
            position={2} />
        </Grid>

        {selectedType === "Individual" && (
          <>
            <Grid item xs={12} sm={6}>
              <DynamicInput
                label={t('common.party.fields.firstName_label')}
                name={PARTIES_FIELDS.FIRST_NAME}
                placeholder={t('common.party.fields.firstName_placeholder')}
                fieldArrayName={PARTIES_FORM_ARRAY_NAME}
                formControl={control}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DynamicInput
                label={t('common.party.fields.lastName_label')}
                name={PARTIES_FIELDS.LAST_NAME}
                placeholder={t('common.party.fields.lastName_placeholder')}
                fieldArrayName={PARTIES_FORM_ARRAY_NAME}
                formControl={control}
                required
              />
            </Grid>
          </>
        )}


        {selectedType === "Company" && (
          <>
            <Grid item xs={12} sm={6}>
              <DynamicInput
                label={t('common.party.fields.company_name_label')}
                name={PARTIES_FIELDS.COMPANY_NAME}
                placeholder={t('common.party.fields.company_name_placeholder')}
                fieldArrayName={PARTIES_FORM_ARRAY_NAME}
                formControl={control}
                required
                onBlur={() => handleFieldBlur(PARTIES_FIELDS.COMPANY_NAME)}
              />
            </Grid>


            <Grid item xs={12} sm={6}>
              <DynamicInput
                label={t('common.party.fields.vat_number_label')}
                name={PARTIES_FIELDS.VAT_NUMBER}
                placeholder={t('common.party.fields.vat_number_placeholder')}
                fieldArrayName={PARTIES_FORM_ARRAY_NAME}
                formControl={control}
                type="number"
                max={15}
                onlyNumbers
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DynamicInput
                label={t('common.party.fields.crn_label')}
                name={PARTIES_FIELDS.CRN}
                placeholder={t('common.party.fields.crn_placeholder')}
                fieldArrayName={PARTIES_FORM_ARRAY_NAME}
                formControl={control}
                type="number"
                max={10}
                onlyNumbers
                onBlur={() => handleFieldBlur(PARTIES_FIELDS.CRN)}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6}>
          <DynamicPhone
            type="text"
            label={t('common.party.fields.contact_label')}
            name={PARTIES_FIELDS.CONTACT_NO}
            placeholder={t('common.party.fields.contact_placeholder')}
            fieldArrayName={PARTIES_FORM_ARRAY_NAME}
            formControl={control}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <DynamicInput
            label={t('common.party.fields.email_label')}
            name={PARTIES_FIELDS.EMAIL_IDS}
            placeholder={t('common.party.fields.email_placeholder')}
            fieldArrayName={PARTIES_FORM_ARRAY_NAME}
            formControl={control}
            onBlur={() => handleFieldBlur(PARTIES_FIELDS.EMAIL_IDS)}
          />
        </Grid>

        {/*Entity Name */}
        <Grid item xs={12} sm={6}>
          <DynamicSearchSelect
            label={t('common.party.fields.company_label')}
            apiType="company"
            selectedLabel='Companies'
            name={PARTIES_FIELDS.COMPANY}
            placeholder={t('common.party.fields.company_placeholder')}
            fieldArrayName={PARTIES_FORM_ARRAY_NAME}
            formControl={control}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <DynamicSearchSelect
            label={t('common.party.fields.currency_label')}
            apiType='currency'
            selectedLabel='Currency'
            name={PARTIES_FIELDS.CURRENCY}
            placeholder={t('common.party.fields.currency_placeholder')}
            fieldArrayName={PARTIES_FORM_ARRAY_NAME}
            formControl={control}
            required
            is_multiselect
          />
        </Grid>

        <Grid item xs={12} sm={6}>
              <DynamicInput
                label={t('common.party.fields.responsible_person_label')}
                name={PARTIES_FIELDS.RESPONSIBLE_PERSON}
                placeholder={t('common.party.fields.responsible_person_placeholder')}
                fieldArrayName={PARTIES_FORM_ARRAY_NAME}
                formControl={control}
                required
              />
        </Grid>


      </Grid>
    </Accordion>
  ), [t, control, selectedType]);

  const accountingSection = useMemo(() => (
    <Accordion
      title={
        <Typography type="s3" weight="medium" color="theme.secondary.1000">
          {t("common.party.sections.accounting")}
        </Typography>
      }
      accordionProps={{
        defaultExpanded: true,
        className: "form--Accordion",
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DynamicSearchSelect
            label={t('common.party.fields.receivable_account_label')}
            apiType="accounts"
            selectedLabel='Account'
            name={PARTIES_FIELDS.RECEIVABLE_ACCOUNT}
            placeholder={t('common.party.fields.receivable_account_placeholder')}
            fieldArrayName={PARTIES_FORM_ARRAY_NAME}
            formControl={control}
            required
            customeFilter={{ '&account_type.eq': 'Receivable' }}
            options={[]}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DynamicSearchSelect
            label={t('common.party.fields.tax_template_label')}
            apiType="taxTemplate"
            selectedLabel='Tax Template'
            name={PARTIES_FIELDS.TAX_TEMPLATE}
            placeholder={t('common.party.fields.tax_template_placeholder')}
            fieldArrayName={PARTIES_FORM_ARRAY_NAME}
            formControl={control}
            required
            options={[]}
          />
        </Grid>
      </Grid>
    </Accordion>
  ), [t, control]);

  const memoizedAddressColumns = useMemo(() => tableColumns(addressColumns(isKSA)), [tableColumns, isKSA]);
  const memoizedContactColumns = useMemo(() => tableColumns(contactColumns), [tableColumns]);

  const addressSection = useMemo(() => (
    <Accordion
      title={
        <Typography type="s3" weight="medium" color="theme.secondary.1000">
          {t("common.party.sections.address")}
          <span className='errorIndicator'>*</span>
          {errors?.[PARTIES_FORM_ARRAY_NAME]?.[PARTIES_FIELDS.ADDRESS] && (
            <span className='error'>
              {errors?.[PARTIES_FORM_ARRAY_NAME]?.[PARTIES_FIELDS.ADDRESS]?.message}
            </span>
          )}
        </Typography>
      }
      accordionProps={{
        defaultExpanded: true,
        className: "form--Accordion",
      }}
    >
      <Grid spacing={4}>
        <Grid item xs={12}>
          <MaterialTable
            rows={addressRows}
            columns={memoizedAddressColumns}
            paginationModel={{ pageNo: 1, limit: 20 }}
            totalPages={20}
            enableColumnDragging={false}
            isResetRow={false}
            columnOrder={['mrt-row-select']}
            enableRowSelection={false}
            enableBottomToolbar
            disableDefaultActionColumn
            editDisplayMode='row'
            enableRowCreate
            onCreateRow={(data) => handleRowOperation(data, addressRows, setAddressRows, true)}
            onEditRow={(data) => handleRowOperation(data, addressRows, setAddressRows, true)}
            onDeleteRow={(row) => deleteRow(addressRows, setAddressRows, row)}
            onChangeRowField={onChangeAddressTableField}
          />
        </Grid>
      </Grid>
    </Accordion>
    // eslint-disable-next-line
  ), [t, addressRows, tableColumns, errors]);

  const contactSection = useMemo(() => (
    <Accordion
      title={
        <Typography type="s3" weight="medium" color="theme.secondary.1000">
          {t("common.party.sections.contact")}
          <span className='errorIndicator'>*</span>
          {errors?.[PARTIES_FORM_ARRAY_NAME]?.[PARTIES_FIELDS.CONTACTS] && (
            <span className='error'>
              {errors?.[PARTIES_FORM_ARRAY_NAME]?.[PARTIES_FIELDS.CONTACTS]?.message}
            </span>
          )}
        </Typography>
      }
      accordionProps={{
        defaultExpanded: true,
        className: "form--Accordion",
      }}
    >
      <Grid spacing={4}>
        <Grid item xs={12}>

          <MaterialTable
            rows={contactRows}
            columns={memoizedContactColumns}
            paginationModel={{ pageNo: 1, limit: 20 }}
            totalPages={20}
            enableColumnDragging={false}
            isResetRow={false}
            columnOrder={['mrt-row-select']}
            enableRowSelection={false}
            enableBottomToolbar
            disableDefaultActionColumn
            editDisplayMode='row'
            enableRowCreate
            onCreateRow={(data) => handleRowOperation(data, contactRows, setContactRows, false)}
            onEditRow={(data) => {
              handleRowOperation(data, contactRows, setContactRows, false);

              if (data.row.index === 0) {
                setValue(
                  `${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.RESPONSIBLE_PERSON}`,
                  data.values.name || ''
                );
                setValue(
                  `${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.CONTACT_NO}`,
                  data.values.contact_no || ''
                );
                setValue(
                  `${PARTIES_FORM_ARRAY_NAME}.${PARTIES_FIELDS.EMAIL_IDS}`,
                  data.values.email || ''
                );
              }
            }}
            onDeleteRow={(row) => {
              if (row.index === 0) {
                enqueueSnackbar(t('common.party.errors.cannotDeleteResponsiblePerson'), { variant: 'error' });
                return Promise.resolve();
              }
              return deleteRow(contactRows, setContactRows, row);
            }}
            onChangeRowField={onChangeAddressTableField}
          />
        </Grid>
      </Grid>
    </Accordion>
    // eslint-disable-next-line
  ), [t, contactRows, tableColumns, errors]);

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      maxWidth='lg'
      fullWidth
      sx={{ overflowY: 'hidden' }}
      {...modalProps}
    >
      {isCreating && <ModalLoader />}
      <form onSubmit={handleSubmit(onSubmit)} className="scroll-container" style={{ position: 'relative', overflow: 'auto' }}>
        <DialogTitle className='itemEntryModal--DialogTitle'
        >
          <Typography type='s3' color='inherit' weight='medium'>
            {`Add ${type}`}
          </Typography>
          <IconButton onClick={closeModal}>
            <Close fontSize='small' />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {basicDetailsSection}
          <Divider className='form--SectionDivider' />
          {addressSection}
          <Divider className='form--SectionDivider' />
          {contactSection}
          <Divider className='form--SectionDivider' />
          {accountingSection}
        </DialogContent>

        <DialogActions className='itemEntryModal--DialogAction' sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'white',
          zIndex: 1,
          padding: '8px 24px',
          justifyContent: 'end'
        }}>
          <Box display='flex' gap={1.5}>
            <Button
              className='itemEntryModal--DialogAction--WhiteBtn'
              onClick={closeModal}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit(onSubmit)}
            >
              {t('common.save')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Modal>
  );
};
